import { sessionRepository, SessionRepository } from './SessionRepository'
import type {
  CreateSessionInput,
  SessionRecord,
  SessionValidationResult,
  UpdateSessionInput,
} from './SessionTypes'

/**
 * SessionService — persistence and lifecycle helpers.
 * No real authentication; connectors must call this instead of owning login state.
 */
export class SessionService {
  private readonly repository: SessionRepository

  constructor(repository: SessionRepository = sessionRepository) {
    this.repository = repository
  }

  list(): SessionRecord[] {
    return this.repository.list()
  }

  listByIntegration(integrationId: string): SessionRecord[] {
    return this.repository.findByIntegrationId(integrationId)
  }

  getSession(id: string): SessionRecord | null {
    return this.repository.findById(id)
  }

  /** Active / newest session for an integration (if any). */
  getActiveSession(integrationId: string): SessionRecord | null {
    const sessions = this.repository.findByIntegrationId(integrationId)
    return (
      sessions.find((s) => s.status === 'active') ??
      sessions.find((s) => s.status === 'authenticating' || s.status === 'pending') ??
      null
    )
  }

  createSession(input: CreateSessionInput): SessionRecord {
    const now = nowIso()
    const record: SessionRecord = {
      id: input.id ?? `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      integrationId: input.integrationId,
      status: input.status ?? 'pending',
      cookies: input.cookies ?? '',
      headers: input.headers ?? {},
      tokens: input.tokens ?? {},
      createdAt: now,
      updatedAt: now,
      expiresAt: input.expiresAt ?? null,
      lastValidation: null,
      lastError: null,
      browser: input.browser ?? {
        contextKey: null,
        storageStateRef: null,
        userAgent: null,
        headless: true,
      },
    }
    return this.repository.upsert(record)
  }

  updateSession(id: string, patch: UpdateSessionInput): SessionRecord | null {
    const current = this.repository.findById(id)
    if (!current) return null
    const next: SessionRecord = {
      ...current,
      ...patch,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: nowIso(),
      headers: patch.headers ?? current.headers,
      tokens: patch.tokens ?? current.tokens,
      browser: patch.browser ?? current.browser,
    }
    return this.repository.upsert(next)
  }

  /**
   * Local validation only (expiry + status). No remote ping in Sprint 2.3.
   * Future: Playwright storage-state / cookie probe hooks.
   */
  validateSession(id: string): SessionValidationResult {
    const session = this.repository.findById(id)
    if (!session) {
      return { valid: false, session: null, reason: 'Sessão não encontrada' }
    }

    if (session.status === 'invalid' || session.status === 'error') {
      const updated = this.updateSession(id, {
        lastValidation: nowIso(),
        lastError: session.lastError ?? `Status ${session.status}`,
      })
      return { valid: false, session: updated, reason: `Sessão em estado ${session.status}` }
    }

    if (session.expiresAt) {
      const expires = Date.parse(session.expiresAt)
      if (!Number.isNaN(expires) && expires <= Date.now()) {
        const updated = this.updateSession(id, {
          status: 'expired',
          lastValidation: nowIso(),
          lastError: 'Sessão expirada',
        })
        return { valid: false, session: updated, reason: 'Sessão expirada' }
      }
    }

    if (session.status !== 'active' && session.status !== 'authenticating') {
      const updated = this.updateSession(id, { lastValidation: nowIso() })
      return {
        valid: false,
        session: updated,
        reason: `Status "${session.status}" não é válido para uso`,
      }
    }

    const updated = this.updateSession(id, {
      lastValidation: nowIso(),
      lastError: null,
    })
    return { valid: true, session: updated, reason: 'Validação local OK (sem login remoto)' }
  }

  invalidateSession(id: string, reason = 'Invalidada pelo Session Manager'): SessionRecord | null {
    return this.updateSession(id, {
      status: 'invalid',
      lastError: reason,
      lastValidation: nowIso(),
    })
  }

  removeSession(id: string): boolean {
    return this.repository.remove(id)
  }
}

function nowIso(): string {
  return new Date().toISOString()
}

export const sessionService = new SessionService()
